<template>
  <div class="campana">
    <v-menu
      v-model="panelAbierto"
      :close-on-content-click="false"
      :nudge-bottom="12"
      :max-width="anchoPanel"
      :min-width="anchoPanel"
      offset-y
      left
      transition="slide-y-transition"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          class="campana__boton"
          aria-label="Notificaciones"
          v-bind="attrs"
          v-on="on"
        >
          <v-badge
            :value="noLeidas > 0"
            :content="noLeidasTexto"
            color="#f25757"
            overlap
            bordered
          >
            <v-icon size="26" color="#1c1e4d">mdi-bell-outline</v-icon>
          </v-badge>
        </v-btn>
      </template>

      <v-card class="campana__panel" flat>
        <div class="campana__encabezado">
          <span class="campana__rotulo">Notificaciones</span>
          <button
            type="button"
            class="campana__marcar"
            :disabled="noLeidas === 0 || cargando"
            @click="marcarTodas"
          >
            Marcar todas como leídas
          </button>
        </div>

        <div class="campana__buscador">
          <v-text-field
            v-model="buscar"
            append-icon="mdi-magnify"
            clearable
            dense
            hide-details
            outlined
            placeholder="Buscar notificación"
          />
        </div>

        <div class="campana__lista">
          <div v-if="cargando" class="campana__estado">
            <v-progress-circular
              indeterminate
              size="24"
              width="2"
              color="#1c1e4d"
            />
          </div>

          <div v-else-if="notificaciones.length === 0" class="campana__estado">
            <v-icon size="36" color="#c5c9d4">mdi-bell-sleep-outline</v-icon>
            <span class="campana__vacio">No tienes notificaciones</span>
          </div>

          <template v-else>
            <div
              v-for="notificacion in notificaciones"
              :key="notificacion._id"
              class="campana__item"
              :class="{ 'campana__item--no-leida': !notificacion.leida }"
              @click="ejecutarAccion(notificacion)"
            >
              <v-icon
                size="20"
                :color="colorSeveridad(notificacion.severidad)"
                class="campana__item-icono"
              >
                {{ iconoSeveridad(notificacion.severidad) }}
              </v-icon>

              <div class="campana__item-cuerpo">
                <span class="campana__item-titulo">
                  {{ notificacion.titulo }}
                </span>
                <span class="campana__item-mensaje">
                  {{ notificacion.mensaje }}
                </span>
                <span class="campana__item-fecha">
                  {{ formatearFecha(notificacion.created_at) }}
                </span>
              </div>

              <v-icon
                v-if="iconoAccion(notificacion.tipo)"
                size="16"
                color="#9aa1b1"
                class="campana__item-accion"
              >
                {{ iconoAccion(notificacion.tipo) }}
              </v-icon>
            </div>
          </template>
        </div>
      </v-card>
    </v-menu>

    <v-dialog v-model="detalleAbierto" max-width="460" scrollable>
      <v-card class="campana__detalle" v-if="detalle">
        <div class="campana__detalle-encabezado">
          <span class="campana__detalle-titulo">{{ detalle.titulo }}</span>
          <v-btn icon small @click="detalleAbierto = false">
            <v-icon size="20" color="#697182">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider />

        <div class="campana__detalle-cuerpo">
          <div
            v-for="(propiedad, indice) in detalle.propiedades"
            :key="indice"
            class="campana__propiedad"
          >
            <span class="campana__propiedad-etiqueta">
              {{ propiedad.etiqueta }}
            </span>
            <span class="campana__propiedad-valor">
              {{ formatearValor(propiedad.valor) }}
            </span>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const ICONOS_SEVERIDAD = {
  info: "mdi-information-outline",
  exito: "mdi-check-circle-outline",
  advertencia: "mdi-alert-outline",
  error: "mdi-alert-circle-outline"
};

const COLORES_SEVERIDAD = {
  info: "#1c1e4d",
  exito: "#45c4b0",
  advertencia: "#f3a723",
  error: "#f25757"
};

const ICONOS_ACCION = {
  REDIRECT: "mdi-arrow-top-right",
  DETAIL: "mdi-chevron-right"
};

export default {
  name: "BellNotification",
  data() {
    return {
      panelAbierto: false,
      detalleAbierto: false,
      cargando: false,
      procesando: false,
      notificaciones: [],
      noLeidas: 0,
      detalle: null,
      buscar: "",
      debounceBuscarTimer: null
    };
  },
  computed: {
    anchoPanel() {
      return this.$vuetify.breakpoint.xs ? 300 : 380;
    },
    noLeidasTexto() {
      return this.noLeidas > 99 ? "99+" : String(this.noLeidas);
    }
  },
  watch: {
    panelAbierto(abierto) {
      if (abierto) {
        this.cargar();
      }
    },
    buscar() {
      clearTimeout(this.debounceBuscarTimer);
      this.debounceBuscarTimer = setTimeout(() => this.cargar(), 1000);
    }
  },
  mounted() {
    this.cargar();
  },
  methods: {
    /**
     * Obtiene el listado de notificaciones vigentes y el conteo de no leídas.
     *
     * @returns {Promise<void>}
     */
    async cargar() {
      if (this.cargando) return;

      this.cargando = true;

      const [listado, conteo] = await Promise.all([
        this.services.notificaciones
          .getNotificaciones({ archivada: false, per_page: 20, buscar: this.buscar })
          .catch(() => null),
        this.services.notificaciones.getConteoNotificaciones().catch(() => null)
      ]);

      this.notificaciones = listado?.data?.data?.data || [];
      this.noLeidas = conteo?.data?.data?.no_leidas || 0;
      this.cargando = false;
    },

    /**
     * Resuelve la acción de una notificación y actualiza su estado de lectura.
     *
     * @param {Object} notificacion Notificación seleccionada por el usuario.
     * @returns {Promise<void>}
     */
    async ejecutarAccion(notificacion) {
      if (this.procesando) return;

      this.procesando = true;

      const respuesta = await this.services.notificaciones
        .ejecutarAccionNotificacion(notificacion._id)
        .catch(() => null);

      this.procesando = false;

      if (!respuesta) return;

      if (!notificacion.leida) {
        notificacion.leida = true;
        this.noLeidas = Math.max(0, this.noLeidas - 1);
      }

      this.resolverAccion(respuesta?.data?.data);
    },

    /**
     * Ejecuta en el cliente la acción devuelta por el backend.
     *
     * @param {Object} accion Acción resuelta según el tipo de notificación.
     */
    resolverAccion(accion) {
      if (!accion) return;

      if (accion.tipo === "REDIRECT" && accion.url) {
        this.panelAbierto = false;
        window.location = this.construirUrlDestino(accion.url);
        return;
      }

      if (accion.tipo === "DETAIL") {
        this.detalle = accion;
        this.detalleAbierto = true;
        this.panelAbierto = false;
      }
    },

    /**
     * Agrega la llave de sesión cuando el destino pertenece a otro origen,
     * igual que la navegación del menú lateral.
     *
     * @param {string} url URL almacenada en la notificación.
     * @returns {string} URL final a la que se debe navegar.
     */
    construirUrlDestino(url) {
      const sessionUuid = localStorage.getItem("session_uuid");

      try {
        const destino = new URL(url, window.location.origin);

        if (destino.origin !== window.location.origin && sessionUuid) {
          destino.searchParams.set("session", sessionUuid);
        }

        return destino.toString();
      } catch {
        return url;
      }
    },

    /**
     * Marca como leídas todas las notificaciones del usuario.
     *
     * @returns {Promise<void>}
     */
    async marcarTodas() {
      const respuesta = await this.services.notificaciones
        .marcarTodasNotificacionesLeidas()
        .catch(() => null);

      if (!respuesta) return;

      this.notificaciones = this.notificaciones.map((notificacion) => ({
        ...notificacion,
        leida: true
      }));
      this.noLeidas = 0;
    },

    iconoSeveridad(severidad) {
      return ICONOS_SEVERIDAD[severidad] || ICONOS_SEVERIDAD.info;
    },

    colorSeveridad(severidad) {
      return COLORES_SEVERIDAD[severidad] || COLORES_SEVERIDAD.info;
    },

    iconoAccion(tipo) {
      return ICONOS_ACCION[tipo] || null;
    },

    /**
     * Expresa la antigüedad de una notificación en formato abreviado.
     *
     * @param {string} fecha Fecha de creación en formato ISO.
     * @returns {string} Antigüedad legible o cadena vacía si la fecha no es válida.
     */
    formatearFecha(fecha) {
      const instante = new Date(fecha);

      if (Number.isNaN(instante.getTime())) return "";

      const segundos = Math.floor((Date.now() - instante.getTime()) / 1000);

      if (segundos < 60) return "hace un momento";

      const minutos = Math.floor(segundos / 60);

      if (minutos < 60) return `hace ${minutos} min`;

      const horas = Math.floor(minutos / 60);

      if (horas < 24) return `hace ${horas} h`;

      const dias = Math.floor(horas / 24);

      if (dias < 7) return `hace ${dias} d`;

      return instante.toLocaleDateString("es-SV", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    },

    formatearValor(valor) {
      if (valor === null || valor === undefined || valor === "") return "—";

      if (typeof valor === "boolean") return valor ? "Sí" : "No";

      return String(valor);
    }
  }
};
</script>

<style scoped lang="scss">
$tinta: #1c1e4d;
$tenue: #697182;

.campana {
  display: inline-flex;
}

.campana__boton {
  transition: transform 0.18s ease;

  &:hover {
    transform: scale(1.08);
  }
}

.campana__panel {
  border-radius: 14px;
  overflow: hidden;
}

.campana__encabezado {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #eceef4;
}

.campana__rotulo {
  color: $tinta;
  font-size: 0.9375rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.campana__marcar {
  background: none;
  border: none;
  color: $tenue;
  cursor: pointer;
  font-size: 0.6875rem;
  letter-spacing: 0.02em;
  padding: 0;
  text-transform: uppercase;
  transition: color 0.18s ease;

  &:hover:not(:disabled) {
    color: $tinta;
    text-decoration: underline;
  }

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
}

.campana__buscador {
  padding: 0.75rem 1rem 0;
}

.campana__lista {
  max-height: 380px;
  overflow-y: auto;
}

.campana__estado {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 2.25rem 1rem;
}

.campana__vacio {
  color: $tenue;
  font-size: 0.8125rem;
}

.campana__item {
  align-items: flex-start;
  border-bottom: 1px solid #f2f3f7;
  cursor: pointer;
  display: flex;
  gap: 0.7rem;
  padding: 0.8rem 1rem;
  position: relative;
  transition: background-color 0.18s ease;

  &:hover {
    background-color: #f5f9ff;
  }

  &:last-child {
    border-bottom: none;
  }
}

.campana__item--no-leida {
  background-color: #fafbff;

  &::before {
    background-color: $tinta;
    border-radius: 0 3px 3px 0;
    bottom: 0.55rem;
    content: "";
    left: 0;
    position: absolute;
    top: 0.55rem;
    width: 3px;
  }

  .campana__item-titulo {
    font-weight: 700;
  }
}

.campana__item-icono {
  margin-top: 0.15rem;
}

.campana__item-cuerpo {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.campana__item-titulo {
  color: $tinta;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.25;
}

.campana__item-mensaje {
  color: $tenue;
  display: -webkit-box;
  font-size: 0.75rem;
  line-height: 1.35;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.campana__item-fecha {
  color: #9aa1b1;
  font-size: 0.6875rem;
  margin-top: 0.1rem;
}

.campana__item-accion {
  margin-top: 0.2rem;
}

.campana__detalle {
  border-radius: 14px;
}

.campana__detalle-encabezado {
  align-items: center;
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  padding: 1rem 0.75rem 1rem 1.25rem;
}

.campana__detalle-titulo {
  color: $tinta;
  font-size: 1rem;
  font-weight: 700;
}

.campana__detalle-cuerpo {
  padding: 0.5rem 1.25rem 1.25rem;
}

.campana__propiedad {
  border-bottom: 1px solid #f2f3f7;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 0.7rem 0;

  &:last-child {
    border-bottom: none;
  }
}

.campana__propiedad-etiqueta {
  color: $tenue;
  flex-shrink: 0;
  font-size: 0.8125rem;
}

.campana__propiedad-valor {
  color: $tinta;
  font-size: 0.8125rem;
  font-weight: 500;
  text-align: right;
  word-break: break-word;
}
</style>
